import {
  Anchor,
  Badge,
  Box,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import {
  IconArrowRight,
  IconCalendarEvent,
  IconFlask2,
  IconPhone,
  IconStethoscope,
} from "@tabler/icons-react";
import { NextGenBrand } from "@evolys/ui";
import type { PublicMarketingSiteDto } from "@evolys/types";

type CareGatewayProps = {
  site: PublicMarketingSiteDto;
  style: React.CSSProperties;
};

const routes = [
  {
    eyebrow: "NextGen Diagnostics",
    title: "Laboratory tests",
    body: "Arrange sample collection, find a laboratory location, and access dependable diagnostic services.",
    href: "/diagnostics",
    action: "Choose laboratory services",
    className: "care-route-diagnostics",
    icon: IconFlask2,
    points: ["Home or in-office collection", "Routine and specialist testing", "Secure result access"],
    badge: undefined,
  },
  {
    eyebrow: "NextGen Medical",
    title: "Doctor visits",
    body: "Prepare for scheduled care in private, fully equipped rooms with connected clinical support.",
    href: "/medical",
    action: "Explore medical care",
    className: "care-route-medical",
    icon: IconStethoscope,
    points: ["Scheduled patient visits", "In-house and visiting doctors", "Connected follow-up care"],
    badge: "Opening soon",
  },
] as const;

export function CareGateway({ site, style }: CareGatewayProps) {
  const profile = site.profile;

  return (
    <Box className="care-gateway" style={style}>
      <header className="care-gateway-header">
        <Container size="xl">
          <Group justify="space-between" py="lg" wrap="nowrap">
            <Anchor className="care-gateway-brand" href="/" aria-label="NextGen home">
              <NextGenBrand
                name="NextGen"
                subtitle="Medical care and laboratory services"
                imageSize={52}
              />
            </Anchor>
            {profile.contactPhone ? (
              <Anchor className="care-gateway-phone" href={`tel:${profile.contactPhone}`}>
                <IconPhone size={18} aria-hidden="true" />
                <span>
                  <small>Questions? Call us</small>
                  <strong>{profile.contactPhone}</strong>
                </span>
              </Anchor>
            ) : null}
          </Group>
        </Container>
      </header>

      <main className="care-gateway-main">
        <Container size="xl">
          <Stack className="care-gateway-intro" gap="md">
            <Text className="care-gateway-kicker">One NextGen. Two ways to care for you.</Text>
            <Title order={1}>What can we help you with today?</Title>
            <Text>
              Start with the service you need. You can move between medical care and laboratory
              services at any time.
            </Text>
          </Stack>

          <SimpleGrid className="care-route-grid" cols={{ base: 1, md: 2 }} spacing="xl">
            {routes.map(
              ({
                action,
                badge,
                body,
                className,
                eyebrow,
                href,
                icon: Icon,
                points,
                title,
              }) => (
                <Anchor className={`care-route ${className}`} href={href} key={href}>
                  <div className="care-route-topline" aria-hidden="true" />
                  <Group className="care-route-heading" justify="space-between" align="flex-start">
                    <span className="care-route-icon" aria-hidden="true">
                      <Icon size={31} stroke={1.8} />
                    </span>
                    {badge ? <Badge variant="light">{badge}</Badge> : null}
                  </Group>
                  <div>
                    <Text className="care-route-eyebrow">{eyebrow}</Text>
                    <Title order={2}>{title}</Title>
                    <Text className="care-route-body">{body}</Text>
                  </div>
                  <div className="care-route-points">
                    {points.map((point) => (
                      <span key={point}>
                        <i aria-hidden="true" />
                        {point}
                      </span>
                    ))}
                  </div>
                  <span className="care-route-action">
                    {action}
                    <IconArrowRight size={20} aria-hidden="true" />
                  </span>
                </Anchor>
              ),
            )}
          </SimpleGrid>

          <Group className="care-gateway-assurance" justify="center" gap="xs">
            <IconCalendarEvent size={18} aria-hidden="true" />
            <Text>Appointments and collections are planned services—not emergency care.</Text>
          </Group>
        </Container>
      </main>

      <footer className="care-gateway-footer">
        <Container size="xl">
          <Group justify="space-between" py="lg">
            <Text size="sm">© {new Date().getFullYear()} NextGen Medical &amp; Diagnostics</Text>
            {profile.contactEmail ? (
              <Anchor href={`mailto:${profile.contactEmail}`}>{profile.contactEmail}</Anchor>
            ) : null}
          </Group>
        </Container>
      </footer>
    </Box>
  );
}
